import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { MapLibreEvent } from "maplibre-gl";
import { default as React, useEffect, useRef, useState } from "react";
import Map, { MapRef, Marker } from "react-map-gl/maplibre";
import ReactCountryFlag from "react-country-flag";
import { useAppSelector } from "src/app/hooks";
import Pin from "src/components/mapView/Pin";
import { LocationDto } from "@braoshzmvavz/picnube-common";

const mapStyleJsons = {
  positron: "https://tiles.openfreemap.org/styles/positron",
};

export default function GlobeLocationsShowcase() {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const mapRef = useRef<MapRef>(null);
  const locationState = useAppSelector((state) => state.location);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [currentLocation, setCurrentLocation] = useState<LocationDto | null>(null);
  const [isFading, setIsFading] = useState(false);

  const onLoadMap = (event: MapLibreEvent) => {
    setIsMapLoaded(true);
  };


  // Reset location index when locations change
  useEffect(() => {
    if (locationState.locations.length > 0) {
      setCurrentLocationIndex(0);
      // Set initial location
      const firstLocation = locationState.locations[0];
      if (firstLocation) {
        setCurrentLocation(firstLocation);
      }
    }
  }, [locationState.locations.length]);

  // Animate through locations
  useEffect(() => {
    if (
      !isMapLoaded ||
      !mapRef?.current ||
      !mapRef.current.isStyleLoaded() ||
      locationState.locations.length === 0
    ) {
      return;
    }

    const locations = locationState.locations;
    if (locations.length === 0) return;

    const animateToLocation = (index: number) => {
      const location = locations[index];
      if (!location || location.latitude === undefined || location.longitude === undefined) {
        return;
      }

      // Update location with fade animation
      setIsFading(true);
      setTimeout(() => {
        setCurrentLocation(location);
        setIsFading(false);
      }, 250); // Half of animation duration

      // Fly to the location
      mapRef.current?.flyTo({
        center: [location.longitude, location.latitude],
        zoom: 4,
        duration: 2000,
      });

      // After 5 seconds, move to the next location
      timeoutRef.current = setTimeout(() => {
        const nextIndex = (index + 1) % locations.length;
        setCurrentLocationIndex(nextIndex);
      }, 5000);
    };

    // Start animation
    animateToLocation(currentLocationIndex);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isMapLoaded, locationState.locations, currentLocationIndex]);



  // Create markers for all locations
  const locationMarkers = locationState.locations
    .filter((location) => location != null)
    .map((location, index) => (
      <Marker
        key={`marker-${location.id || index}`}
        longitude={location.longitude}
        latitude={location.latitude}
        anchor="bottom"
      >
        <Pin
          selected={index === currentLocationIndex}
          color="#9ca3af"
          selectedColor="#1971c2"
          mapPoint={location}
        />
      </Marker>
    ));

  return (
    <div className="flex flex-col w-full h-full gap-4">
      <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden mt-[1rem] relative">
        <Map
          id="mapData2"
          initialViewState={{
            longitude: 9.389839,
            latitude: 50.622884 - 10,
            zoom: 1,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyleJsons.positron}
          onLoad={(event: MapLibreEvent) => {
            onLoadMap(event);
          }}
          interactive={false}
          projection={"globe"}
          attributionControl={false}
          minZoom={1}
          maxZoom={5}
          ref={mapRef}
        >
          {locationMarkers}

          {/* <FullscreenControl position="top-left" /> */}

          {/* <MapOverlayButton
            icon={<HiArrowRight />}
            style={{
              pointerEvents: "all",
            }}
            onClick={() => {
              navigate("/map");
            }}
          /> */}
        </Map>
        
        {/* Location name display with fade animation */}
        {currentLocation && (
          <div
            className={`absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2 bg-black/70 text-white rounded-lg text-md font-semibold backdrop-blur-sm transition-opacity duration-500 flex items-center gap-2 ${
              isFading ? "opacity-0" : "opacity-100"
            }`}
          >
            <ReactCountryFlag
              style={{ fontFamily: "Twemoji Country Flags" }}
              countryCode={currentLocation.countryCode}
            />
            <span>{currentLocation.name}</span>
          </div>
        )}
      </div>
    </div>
  );
}
